import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { createJsonValidator } from '../validators/createJsonValidator';

const validatorFactory = {
  json: createJsonValidator,
};

const createDefaultValidator = () => {
  const validate = () => null;
  validate.formatErrors = () => [];
  return validate;
};

export const useValidator = ({ strategy = 'json', schema } = {}) => {
  const { t, i18n } = useTranslation();
  const [errors, setErrors] = useState([]);

  const { localize, parse, validate } = useMemo(() => {
    const createValidator = validatorFactory[strategy];
    if (createValidator && schema) return createValidator({ schema });

    return {
      localize: () => null,
      parse: () => null,
      validate: createDefaultValidator(),
    };
  }, [strategy, schema]);

  useEffect(() => {
    localize(i18n.language, validate.errors);
    setErrors(validate.formatErrors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleValidate = useCallback(
    data => {
      try {
        const parsed = parse(data);
        const valid = validate(parsed);

        if (!valid) {
          localize(i18n.language, validate.errors);
          setErrors(validate.formatErrors());
        } else {
          setErrors([]);
        }

        return { data: parsed, isValid: valid };
      } catch {
        setErrors([
          {
            message: t('validation.invalid_json'),
          },
        ]);
        return { data, isValid: false };
      }
    },
    [t, i18n.language, localize, parse, validate, setErrors],
  );

  return { errors, setErrors, handleValidate };
};
